// MEGA-SPRINT Sprint 6 (V-3 Spelarvy) — client-side iCalendar VEVENT export.
//
// Generates a single-event .ics file for a league match. Client-side only;
// no server roundtrip. The browser downloads the file via a synthesized
// `<a download>` click.
//
// VEVENT shape (RFC 5545 minimal):
//   BEGIN:VCALENDAR
//   VERSION:2.0
//   PRODID:-//Boply//Match Reminder//SE
//   BEGIN:VEVENT
//   UID:match-<match_id>@boply.app
//   DTSTAMP:<now in UTC>
//   DTSTART:<match start in UTC>
//   DTEND:<match end in UTC, +90 min default>
//   SUMMARY:<league name> — <home team> vs <away team>
//   DESCRIPTION:<opponent info + league context + Boply link>
//   LOCATION:<court name or "Boply hall">
//   END:VEVENT
//   END:VCALENDAR
//
// Times stored as Stockholm-local "YYYY-MM-DD" + "HH:MM:SS" are converted
// to UTC via Date.UTC + CET offset (worst-case ±1h skew at DST boundaries;
// acceptable for calendar imports per existing reminder edge fn convention).

export interface IcsMatchEvent {
  matchId: string;
  scheduledDate: string;  // "YYYY-MM-DD"
  scheduledTime: string;  // "HH:MM:SS" or "HH:MM"
  durationMinutes?: number;  // defaults to 90
  leagueName: string;
  homeTeamName: string | null;
  awayTeamName: string | null;
  courtName: string | null;
  opponentLabel?: string;
  matchDeepLink?: string;
}

/**
 * Convert a Stockholm-local date+time pair to UTC `Date`. Mirrors the
 * convention used by `send-league-match-reminder/index.ts`: treat the
 * stored times as Stockholm-local and approximate UTC offset via CET (+1h).
 * Worst-case DST-boundary skew is ±1h. Good enough for calendar imports.
 */
function stockholmLocalToUtc(date: string, time: string): Date {
  const [hh, mm] = time.split(":").map(Number);
  const [yy, mo, dd] = date.split("-").map(Number);
  const cetOffsetMs = 60 * 60 * 1000;
  return new Date(Date.UTC(yy, mo - 1, dd, hh ?? 0, mm ?? 0) - cetOffsetMs);
}

/** Format Date as iCalendar UTC timestamp: YYYYMMDDTHHMMSSZ */
function icsTimestamp(d: Date): string {
  const pad = (n: number, w = 2) => String(n).padStart(w, "0");
  return (
    pad(d.getUTCFullYear(), 4) +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    "T" +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    "Z"
  );
}

/** Escape RFC 5545 TEXT property value (commas, semicolons, newlines). */
function icsEscape(s: string): string {
  return s
    .replace(/\\/g, "\\\\")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;")
    .replace(/\r?\n/g, "\\n");
}

/**
 * Build the .ics file content for a single league match. Returns the
 * raw string (caller wraps in Blob + download anchor).
 */
export function buildMatchIcs(ev: IcsMatchEvent): string {
  const start = stockholmLocalToUtc(ev.scheduledDate, ev.scheduledTime);
  const duration = ev.durationMinutes ?? 90;
  const end = new Date(start.getTime() + duration * 60_000);
  const stamp = icsTimestamp(new Date());

  const summary = ev.opponentLabel
    ? `${ev.leagueName} — vs ${ev.opponentLabel}`
    : `${ev.leagueName} — ${ev.homeTeamName ?? "Hemma"} vs ${ev.awayTeamName ?? "Borta"}`;

  const description = [
    `Liga: ${ev.leagueName}`,
    ev.opponentLabel ? `Motståndare: ${ev.opponentLabel}` : null,
    ev.courtName ? `Bana: ${ev.courtName}` : null,
    ev.matchDeepLink ? `Detaljer: ${ev.matchDeepLink}` : null,
  ]
    .filter((line): line is string => line !== null)
    .join("\\n");

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Boply//Match Reminder//SE",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:match-${ev.matchId}@boply.app`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${icsTimestamp(start)}`,
    `DTEND:${icsTimestamp(end)}`,
    `SUMMARY:${icsEscape(summary)}`,
    `DESCRIPTION:${icsEscape(description)}`,
    ev.courtName ? `LOCATION:${icsEscape(ev.courtName)}` : "LOCATION:Boply",
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return lines.join("\r\n") + "\r\n";
}

/**
 * Trigger a client-side download of the given .ics content. No-op outside
 * a browser environment.
 */
export function downloadIcsFile(filename: string, content: string): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".ics") ? filename : `${filename}.ics`;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
